import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AlertBox from '../components/AlertBox';
import ReCAPTCHA from 'react-google-recaptcha';
import { API_BASE_URL, RECAPTCHA_SITE_KEY } from '../config';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA.');
      setIsLoading(false);
      return;
    }
    try {
      // Call backend to check email and generate PIN
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, recaptchaToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      // Redirect to PIN entry page, pass email as state
      navigate('/verify-pin', { state: { email } });
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-yellow-50 flex items-center justify-center py-12">
      <div className="max-w-md w-full mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/img/core-img/logo-white.png" alt="SoundX724 Logo" className="w-64 h-64 object-contain" />
          </div>
          <h2 className="text-3xl font-elegant font-bold text-secondary-900 mb-2">
            Welcome Back
          </h2>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && <AlertBox type="error" message={error} />}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                placeholder="Enter your email"
                required
              />
            </div>

            {/* Google reCAPTCHA */}
            <div>
              <ReCAPTCHA
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={setRecaptchaToken}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-600 hover:bg-primary-500 disabled:bg-primary-400 text-white py-3 rounded-lg font-semibold transition-colors"
            >
              {isLoading ? 'Sending PIN...' : 'Send PIN'}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-secondary-300"></div>
            </div>
          </div>
        </div>

        {/* Additional Links */}
        <div className="text-center mt-6 space-y-2">
          <Link to="/" className="text-secondary-500 hover:text-primary-600 text-sm transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;